import {
  GithubIcon,
  Globe2Icon,
  LinkedinIcon,
  Loader,
  SearchCheckIcon,
  TwitterIcon,
  UserIcon,
  MailIcon,
  MessageSquare,
  Hash,
  ViewIcon,
  ScanEyeIcon,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useWallet } from "../../context/WalletContext";
import { innovatorAPI } from "../../services/api";
import { toast } from "react-toastify";
import skyLoader from "../../assets/home-assets/skyLoader.gif";

// Custom icon components for platforms not available in lucide-react
const TelegramIcon = (props) => {
  // Using MessageSquare as a fallback for Telegram
  return <MessageSquare {...props} title="Telegram" />;
};

const DiscordIcon = (props) => {
  // Using Hash as a fallback for Discord
  return <Hash {...props} title="Discord" />;
};

function Innovators() {
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [innovators, setInnovators] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isConnected, walletAddress, connectWallet, isRegisteredInnovator } =
    useWallet();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("register") === "true") {
      if (!isConnected) {
        connectWallet();
      } else if (!isRegisteredInnovator) {
        setShowForm(true);
      }
    }
  }, [location, isConnected, isRegisteredInnovator, connectWallet]);

  const fetchInnovators = async () => {
    try {
      setLoading(true);
      const response = await innovatorAPI.getAll();
      setInnovators(response.data);
    } catch (error) {
      toast.error(`Error fetching innovators: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchInnovators();
  }, []);

  const handleRegisterClick = async () => {
    if (!isConnected) {
      await connectWallet();
    }
    setShowForm(true);
  };

  const handleSubmitSuccess = (data) => {
    setShowForm(false);
    fetchInnovators();
    navigate("/innovators");
  };

  const handleInnovatorClick = (innovator) => {
    navigate(`/innovators/${innovator.walletAddress}`);
  };

  const getSocialLink = (platform, username) => {
    if (!username) return null;
    switch (platform) {
      case "email":
        return `mailto:${username}`;
      case "github":
        return `https://github.com/${username}`;
      case "linkedin":
        return `https://linkedin.com/in/${username}`;
      case "twitter":
        return `https://twitter.com/${username}`;
      case "website":
        return username;
      case "telegram":
        return `https://t.me/${username}`;
      case "discord":
        return `https://discord.com/users/${username}`;
      default:
        return null;
    }
  };

  const platformIcons = {
    github: <GithubIcon size={18} />,
    linkedin: <LinkedinIcon size={18} />,
    twitter: <TwitterIcon size={18} />,
    website: <Globe2Icon size={18} />,
    email: <MailIcon size={18} />,
    telegram: <TelegramIcon size={18} />,
    discord: <DiscordIcon size={18} />,
  };

  const filteredInnovators = innovators.filter((innovator) => {
    // First filter out the current user
    if (walletAddress && innovator.walletAddress?.toLowerCase() === walletAddress.toLowerCase()) {
      return false;
    }

    // Then apply search and industry filters
    const matchesSearch =
      searchQuery.trim() === "" ||
      innovator.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      innovator.skills?.some((skill) =>
        skill.toLowerCase().includes(searchQuery.toLowerCase())
      );

    const matchesIndustry =
      selectedIndustry === "all" ||
      innovator.domains?.some(
        (domain) => domain.toLowerCase() === selectedIndustry.toLowerCase()
      );

    return matchesSearch && matchesIndustry;
  });

  return (
    <div className="min-h-screen p-6 bg-gradient-to-b from-indigo-50 via-white to-indigo-100">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-black">Innovators</h1>
        {/* {!isRegisteredInnovator && (
          <button
            onClick={handleRegisterClick}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-2 px-5 rounded-lg shadow-md hover:shadow-lg hover:scale-105 transition-all"
          >
            Register as Innovator
          </button>
        )} */}
      </div>

      {/* Search and Filters */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          {/* Search Input */}
          <div className="relative w-full sm:w-2/3">
            <input
              type="text"
              className="w-full p-3 pl-10 border rounded-lg focus:ring-2 focus:ring-indigo-400 shadow-sm"
              placeholder="Search innovators by name or wallet address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchCheckIcon
              className="absolute left-3 top-3 text-gray-400"
              size={20}
            />
          </div>

          {/* Dropdown Filter */}
          <select
            className="w-full sm:w-1/3 p-3 border rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-400"
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
          >
            <option value="all">All Industries</option>
            <option value="AI & Machine Learning">AI & Machine Learning</option>
            <option value="DeFi & Finance">DeFi & Finance</option>
            <option value="Climate & Sustainability">
              Climate & Sustainability
            </option>
            <option value="Gaming & Entertainment">
              Gaming & Entertainment
            </option>
            <option value="Infrastructure & Tooling">
              Infrastructure & Tooling
            </option>
            <option value="Digital Arts & NFTs">Digital Arts & NFTs</option>
            <option value="Security & Privacy">Security & Privacy</option>
            <option value="Social Impact & Community">
              Social Impact & Community
            </option>
            <option value="Web3 & Blockchain">Web3 & Blockchain</option>
            <option value="Education & Knowledge">Education & Knowledge</option>
            <option value="Health & Wellness">Health & Wellness</option>
            <option value="Supply Chain & Logistics">
              Supply Chain & Logistics
            </option>
            <option value="Governance & DAOs">Governance & DAOs</option>
            <option value="Identity & Credentials">
              Identity & Credentials
            </option>
            <option value="Metaverse & Virtual Worlds">
              Metaverse & Virtual Worlds
            </option>
            <option value="Hardware & IoT">Hardware & IoT</option>
          </select>
        </div>
      </div>

      {/* Innovators Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-100">
          <img 
            src={skyLoader} 
            alt="Loading..." 
            className="w-100 h-100 object-contain"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {filteredInnovators?.length > 0 ? (
            filteredInnovators?.map((innovator) => (
              <div
                key={innovator.walletAddress}
                onClick={() => handleInnovatorClick(innovator)}
                className="flex flex-col justify-between bg-gradient-to-br from-white via-indigo-100 to-purple-100 rounded-2xl shadow-md hover:shadow-2xl p-6 transform hover:scale-105 transition-all duration-300 h-full"
              >
                {/* Top Section */}
                <div>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <h3 className="text-xl font-bold text-indigo-700">
                        {innovator.name || "Name not provided"}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {innovator.roles?.join(", ") || "Roles not specified"}
                      </p>
                    </div>
                    {innovator?.profileImage ? (
                      <img
                        src={innovator.profileImage}
                        alt={innovator.name}
                        className="w-14 h-14 rounded-full border-2 border-indigo-400"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                        <UserIcon size={24} className="text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Skills */}
                  {innovator.skills?.length > 0 ? (
                    <div className="mb-4 flex flex-wrap gap-2">
                      {innovator.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="bg-indigo-100 text-indigo-700 text-xs font-medium px-3 py-1 rounded-full"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm mb-4 italic">
                      Not added yet any skill
                    </p>
                  )}

                  {/* Domains */}
                  <p className="text-gray-600 text-sm mb-6 line-clamp-2">
                    {innovator.domains?.join(", ") || "Domains not specified"}
                  </p>
                </div>

                {/* Footer: Always at Bottom */}
                <div className="mt-auto pt-4 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    {/* Socials */}
                    <div className="flex gap-3 text-indigo-600 justify-center sm:justify-start">
                      {Object.keys(platformIcons).map((platform) => {
                        const link = getSocialLink(
                          platform,
                          innovator?.socials?.[platform]
                        );
                        const icon = platformIcons[platform];
                        return (
                          <a
                            key={platform}
                            href={link || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-purple-600 text-lg"
                          >
                            {icon}
                          </a>
                        );
                      })}
                    </div>

                    {/* Button */}
                    <button className="w-full sm:w-auto text-center text-white rounded-full p-2 bg-blue-400 text-sm hover:text-purple-600 transition flex items-center justify-center">
                      <ScanEyeIcon size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full flex justify-center items-center h-64">
              <p className="text-gray-400 text-lg">No Innovators Found</p>
            </div>
          )}
        </div>
      )}

      {/* Registration Form Modal */}
      {/* {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl relative">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-indigo-700">
                Register as Innovator
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-800 text-2xl"
              >
                ×
              </button>
            </div>
            <InnovatorForm
              onSubmitSuccess={handleSubmitSuccess}
              connectedWallet={walletAddress}
              onClose={() => setShowForm(false)}
            />
          </div>
        </div>
      )} */}
    </div>
  );
}

export default Innovators;
